/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  AssetUtils,
  Channel,
  Episode,
  Movie,
  Program,
  RelativeTime,
  Series,
  TvAsset,
  TvAssetUtils,
  Vod,
} from '@wit/vtv-x-foundation-lib';
import React, { useCallback, useState } from 'react';
import { AssetActionButton } from 'types/components/ActionButtonsGroup';
import translate from 'services/i18n/translate';

import TvEventDetails from './TvEventDetails';
import Header from './Header';
import LiveEventDetails from './LiveEventDetails';
import OnDemandEventDetails from './OnDemandEventDetails';
import { ActionButtonsGroup, BadgesGroup } from '..';
import PIOptionsBadgeEngine from '../../../core/services/core/badgeEngine/PIOptionsBadgeEngine';
import useIsAdultContent from '../../../hooks/useIsAdultContent';
import { PiContext } from '../../../store/ducks/programInfo';

interface Props {
  ctx: PiContext;
  channel?: Channel;
  asset?: Asset;
  actionButtons?: AssetActionButton[];
  liveEventNextPrograms?: Program[];
}

function renderEventDetailsByEventType(
  channel?: Channel,
  liveEvent?: Program,
  tvEvent?: TvAsset,
  onDemandEvent?: Vod | Series,
  liveEventNextPrograms?: Program[]
): JSX.Element | null {
  if (channel && liveEvent) {
    return <LiveEventDetails nextPrograms={liveEventNextPrograms || []} />;
  }

  if (tvEvent) {
    return <TvEventDetails event={tvEvent} channel={channel} />;
  }

  if (!onDemandEvent) {
    return null;
  }

  if (channel && AssetUtils.isTvAsset(onDemandEvent)) {
    return <TvEventDetails channel={channel} event={onDemandEvent} />;
  }

  return <OnDemandEventDetails event={onDemandEvent as Vod} />;
}

function renderHeaderByEventType(liveEvent?: Program, tvEvent?: TvAsset, onDemandEvent?: Vod | Series): JSX.Element {
  if (liveEvent) {
    return <Header liveEvent={liveEvent} />;
  }
  if (tvEvent) {
    return <Header tvEvent={tvEvent} />;
  }

  return <Header onDemandEvent={onDemandEvent} />;
}

export default function ProgrammeInfo({
  ctx,
  channel,
  asset,
  actionButtons = [],
  liveEventNextPrograms,
}: Props): JSX.Element {
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const isAdultContent = useIsAdultContent(asset);

  const synopsis = useCallback(() => {
    if (isAdultContent) {
      return translate['asset.adult.content'];
    }
    return asset?.synopsis ? asset?.synopsis : translate['watchtv.programInformationUnavailable'];
  }, [asset?.synopsis, isAdultContent]);

  if (!asset || isAdultContent === undefined) {
    return <></>;
  }

  const badge = PIOptionsBadgeEngine.UI.getBadging(ctx, actionButtons[activeOptionIndex]);

  // TODO: this logic was moved from the PI main page to avoid full page redraws when an option was hovered, it needs to be improved
  let liveEvent: Program | undefined;
  let onDemandEvent: Movie | Episode | undefined;
  let tvEvent: TvAsset | undefined;
  if (
    AssetUtils.isEpg(asset) &&
    channel &&
    AssetUtils.isChannel(channel) &&
    TvAssetUtils.getRelativeTime(asset) === RelativeTime.LIVE
  ) {
    liveEvent = asset;
  } else if (AssetUtils.isVod(asset)) {
    onDemandEvent = asset;
  } else if (AssetUtils.isTvAsset(asset)) {
    tvEvent = asset;
  }

  return (
    <div className="programme-info" id="ProgrammeInfo">
      <div className={`programme-info-container component-inner ${tvEvent || onDemandEvent ? 'fit' : ''}`}>
        <div className="event-details">
          {renderHeaderByEventType(liveEvent, tvEvent, onDemandEvent)}
          <p id="ProgrammeInfo_synopsis" className="episode-synopsis">
            {synopsis()}
          </p>

          <BadgesGroup className="pi-badges-group" badges={badge ? [badge] : []} bemBlock="ProgrammeInfoBadges" />

          {actionButtons?.length && !isAdultContent ? (
            <ActionButtonsGroup
              data={actionButtons}
              onMouseOver={setActiveOptionIndex}
              bemBlock="ProgrammeInfoButtons"
            />
          ) : null}
        </div>
        <div className="event-type-details">
          {renderEventDetailsByEventType(channel, liveEvent, tvEvent, onDemandEvent, liveEventNextPrograms)}
        </div>
      </div>
    </div>
  );
}
